import DimensionUtils from "../DimensionUtils";
import IUnitsTypeDescriptor from "../../database/interfaces/paramScript/IUnitsTypeDescriptor";

/**
 * Egy méret-mező (`units` descriptor + eltárolt "600mm"-alakú érték) feloldása a NumericInput
 * propjaira — EGY helyen, minden méret-kártyának (Size, BackPassing depth/margin, Railing width).
 *
 * ALAPSZABÁLY: üzleti érték (default, min, max, lépések, egység-név) KIZÁRÓLAG a type descriptorból
 * jön, a kódban nincs beégetve. Ahol a descriptor nem ad meg valamit, a fallback SZÁNDÉKOSAN
 * strukturális/semleges, nem egy kitalált méret:
 *   - `max`        → gyakorlatilag korlátlan (Number.MAX_SAFE_INTEGER), nem egy kitalált plafon.
 *     (A NumericInput `max` propja kötelező, "unbounded" opció nincs.)
 *   - `min`        → 0: fizikai méret nem lehet negatív. Ez a mezőtípus természete, nem termék-szabály.
 *   - `smallStep`  → a `decimals`-ből adódó legkisebb lépés (10^-decimals), tehát szintén adatból.
 *   - `largeStep`  → a `smallStep`, ha a descriptor nem ad külön nagy lépést (nincs kitalált multiplier).
 *   - `decimals`   → 0: egész számként jelenítjük meg, ha a descriptor nem mond mást.
 *   - a szám       → az eltárolt érték; ha nincs/nem parse-olható, a descriptor `default`-ja, végül `min`.
 *   - az egység    → az eltárolt érték egysége, HA a descriptor felsorolja; egyébként a descriptor
 *     ELSŐ egysége (így egy érvénytelenné vált egység nem szivárog ki a UI-ba sem).
 */
export interface IResolvedUnitConfig {
    /** A megjelenített egység (a descriptorban létező név). */
    unit: string;
    /** A megjelenített szám. */
    num: number;
    min: number;
    max: number;
    smallStep: number;
    largeStep: number;
    decimals: number;
    /**
     * A descriptor "fix" egységet ír le (min === max, pl. a munkalap-mélység "auto"-ja, aminek az
     * értéke mindig 1): ilyenkor a numerikus mező zárolható, csak az egységváltó marad aktív.
     */
    isFixed: boolean;
    /** Felirat a szám helyett, ha az egység így deklarálja (pl. "automatikus" az auto egységnél). */
    valueLabel?: string;
}

/** A descriptorban felsorolt egység-nevek (a felsorolás SORRENDJE is adat: az első a fallback). */
export const unitNamesOf = (units?: IUnitsTypeDescriptor): string[] => Object.keys(units ?? {});

/**
 * A FALLBACK egység: ez jön elő, ha az érték üres vagy ismeretlen egységű.
 *
 * NEM a kulcssorrend dönti el. A descriptorok JSON-ban tárolódnak, a MySQL pedig NEM őrzi meg a kulcsok
 * beírási sorrendjét (hossz, majd bináris rend szerint tárol) — a `%` (0x25) így a `*` (0x2A) elé kerül.
 * Ezért a `weight` jelölő (a maradékot felszívó egység) az elsődleges, utána az első `proportional`, és
 * csak végül az első kulcs. Jelöletlen `units` blokkoknál ez pontosan a korábbi viselkedés.
 */
export const defaultUnitName = (units: IUnitsTypeDescriptor | undefined): string | undefined => {
    const names = unitNamesOf(units);
    return names.find((name) => (units ?? {})[name]?.weight === true)
        ?? names.find((name) => (units ?? {})[name]?.proportional === true)
        ?? names[0];
};

export const resolveUnitConfig = (
    units: IUnitsTypeDescriptor | undefined,
    valueStr: string
): IResolvedUnitConfig => {
    const names = unitNamesOf(units);
    const parsed = DimensionUtils.parseDimension(String(valueStr ?? ""));
    const unit = names.includes(parsed.unit) ? parsed.unit : (defaultUnitName(units) ?? "");
    const cfg = (units ?? {})[unit] ?? {};

    const decimals = cfg.decimals ?? 0;
    const smallStep = cfg.smallStep ?? Math.pow(10, -decimals);
    const min = cfg.min ?? 0;

    return {
        unit,
        num: !isNaN(parsed.num) ? parsed.num : (cfg.default ?? min),
        min,
        max: cfg.max ?? Number.MAX_SAFE_INTEGER,
        smallStep,
        largeStep: cfg.largeStep ?? smallStep,
        decimals,
        isFixed: cfg.min !== undefined && cfg.max !== undefined && cfg.min === cfg.max,
        valueLabel: cfg.valueLabel,
    };
};

export default resolveUnitConfig;
